import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  cancelSceneToVideoJob,
  createSceneToVideoJob,
  getActiveSceneToVideoJob,
  getSceneToVideoJob,
  retryFailedSceneToVideoScenes,
  generateSceneToVideoDialogue,
  listVideoProviders,
} from "@/lib/api";
import UploadScenePromptsButton from "@/components/UploadScenePromptsButton";
import JobProgressBanner from "@/components/JobProgressBanner";
import VideoProviderSelect from "@/components/VideoProviderSelect";
import ProviderShootoutModal from "@/components/ProviderShootoutModal";

const MAX_SCENES = 20;
const ACCEPT = "image/png,image/jpeg,image/jpg,image/webp";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Scene → Video Agent.
 *
 * Standalone tool (sibling to AutoVideo). Upload 1–20 still images,
 * each gets sent serially to Google Veo 3 → 8-second animated clip.
 * Live polling of the running job — thumbnails + per-scene status
 * stream in as each clip lands.
 */
export default function SceneToVideo() {
  // ---------- Local uploader form state ----------
  const [files, setFiles] = useState([]);          // File[] — pending uploads
  const [previews, setPreviews] = useState([]);    // string[] object URLs
  const [prompts, setPrompts] = useState([]);      // string[] per-scene overrides
  const [defaultPrompt, setDefaultPrompt] = useState(
    "Smooth, natural motion. Cinematic camera move. "
    + "Keep all subjects visually consistent with the input image.",
  );
  const [title, setTitle] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  // Parsed scene descriptions from an uploaded .txt/.docx/.pdf,
  // kept alive so the user can re-map them to a different starting
  // scene without re-uploading.
  const [parsedDocPrompts, setParsedDocPrompts] = useState([]);
  // `descStartScene` = source-script scene number that the FIRST
  // still represents. e.g. user uploaded stills for scenes 5–11 →
  // descStartScene = 5 so still[0] gets parsedDocPrompts[4].
  const [descStartScene, setDescStartScene] = useState(1);
  // Cancel-job state.
  const [cancelling, setCancelling] = useState(false);
  // Retry-failed-scenes state.
  const [retrying, setRetrying] = useState(false);
  // Dialogue-generation state.
  const [generatingDialogue, setGeneratingDialogue] = useState(false);
  // User-selected image-to-video provider for the next job.
  const [videoProvider, setVideoProvider] = useState("");
  // Provider Shootout modal state.
  const [shootoutOpen, setShootoutOpen] = useState(false);
  const [shootoutSceneIdx, setShootoutSceneIdx] = useState(0);

  // ---------- Job polling state ----------
  const [job, setJob] = useState(null);
  const pollTimerRef = useRef(null);

  // ---------- Lifecycle: load any active/last job ----------
  const refresh = useCallback(async () => {
    try {
      const { job: j } = await getActiveSceneToVideoJob();
      setJob(j || null);
    } catch (err) {
      console.debug("STV active poll failed:", err?.message);
    }
  }, []);

  const pollUntilDone = useCallback((jobId) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(async () => {
      try {
        const { job: j } = await getSceneToVideoJob(jobId);
        if (j) setJob(j);
        if (j && (j.status === "complete" || j.status === "failed")) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          if (j.status === "complete") {
            const okCount = (j.scenes || []).filter(
              (s) => s.status === "ready",
            ).length;
            const total = (j.scenes || []).length;
            toast.success(
              `Job done — ${okCount}/${total} scene${total === 1 ? "" : "s"} animated`,
            );
          } else {
            toast.error("Job failed — see per-scene errors");
          }
        }
      } catch (err) {
        console.debug("STV poll failed:", err?.message);
      }
    }, 4000);
  }, []);

  useEffect(() => {
    refresh();
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [refresh]);

  // When an active job exists & it's running, kick off polling.
  useEffect(() => {
    if (job && job.status === "running" && job.job_id) {
      pollUntilDone(job.job_id);
    }
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [job?.job_id, job?.status, pollUntilDone]);

  // Revoke object URLs on unmount / when previews change.
  useEffect(() => {
    return () => {
      previews.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch { /* noop */ }
      });
    };
  }, [previews]);

  // ---------- File picker ----------
  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []).slice(0, MAX_SCENES);
    if (picked.length === 0) return;
    if (files.length + picked.length > MAX_SCENES) {
      toast.error(`Max ${MAX_SCENES} stills per job`);
      e.target.value = "";
      return;
    }
    const nextFiles = [...files, ...picked];
    const nextPreviews = [
      ...previews,
      ...picked.map((f) => URL.createObjectURL(f)),
    ];
    const nextPrompts = [...prompts, ...picked.map(() => "")];
    setFiles(nextFiles);
    setPreviews(nextPreviews);
    setPrompts(nextPrompts);
    e.target.value = "";
  };

  const removeScene = (idx) => {
    setFiles((arr) => arr.filter((_, i) => i !== idx));
    setPreviews((arr) => {
      try { URL.revokeObjectURL(arr[idx]); } catch { /* noop */ }
      return arr.filter((_, i) => i !== idx);
    });
    setPrompts((arr) => arr.filter((_, i) => i !== idx));
  };

  const moveScene = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= files.length) return;
    const swap = (arr) => {
      const next = [...arr];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    };
    setFiles(swap);
    setPreviews(swap);
    setPrompts(swap);
  };

  const clearAll = () => {
    previews.forEach((u) => {
      try { URL.revokeObjectURL(u); } catch { /* noop */ }
    });
    setFiles([]);
    setPreviews([]);
    setPrompts([]);
    setTitle("");
    setUploadPct(0);
  };

  const submit = async () => {
    if (files.length === 0) return; // submit button is disabled — defensive only
    setSubmitting(true);
    setUploadPct(0);
    try {
      const r = await createSceneToVideoJob({
        files,
        prompts,
        defaultPrompt,
        title,
        videoProvider,
        onProgress: setUploadPct,
      });
      toast.success(`Job ${r.job_id} started`);
      clearAll();
      await refresh();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || "Upload failed",
      );
    } finally {
      setSubmitting(false);
      setUploadPct(0);
    }
  };

  const running = job?.status === "running";

  const onCancelJob = useCallback(async () => {
    if (!job?.job_id || job?.status !== "running") return;
    if (!window.confirm("Stop this animation job? Currently-animating scenes will still finish, but no more will be scheduled.")) {
      return;
    }
    setCancelling(true);
    try {
      await cancelSceneToVideoJob(job.job_id);
      toast.success("Stop requested — finishing the current scene…");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't stop the job");
    } finally {
      setCancelling(false);
    }
  }, [job?.job_id, job?.status, refresh]);

  const onRetryFailed = useCallback(async (providerOverride) => {
    if (!job?.job_id) return;
    const failedN = (job.scenes || []).filter((s) => s.status === "failed").length;
    if (failedN === 0) {
      toast.info("No failed scenes to retry");
      return;
    }
    setRetrying(true);
    try {
      const res = await retryFailedSceneToVideoScenes(job.job_id, {
        videoProvider: providerOverride || null,
      });
      toast.success(
        `Retrying ${res.retried_scenes} scene${res.retried_scenes === 1 ? "" : "s"} on ${res.video_provider}`,
      );
      await refresh();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || "Couldn't retry failed scenes",
      );
    } finally {
      setRetrying(false);
    }
  }, [job?.job_id, job?.scenes, refresh]);

  const onGenerateDialogue = useCallback(async ({ onlyFailed = false } = {}) => {
    if (!job?.job_id) return;
    const scope = onlyFailed ? "failed scenes" : "every scene";
    if (!window.confirm(
      `Rewrite ${scope}' prompt with character dialogue + audio cues? ` +
      "This won't re-render anything — you still need to click Retry " +
      "(or start a new job) afterwards to see the dialogue in the video.",
    )) {
      return;
    }
    setGeneratingDialogue(true);
    try {
      const res = await generateSceneToVideoDialogue(job.job_id, { onlyFailed });
      toast.success(
        `Rewrote ${res.updated_scenes} prompt${res.updated_scenes === 1 ? "" : "s"} with dialogue`,
      );
      await refresh();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || "Couldn't generate dialogue",
      );
    } finally {
      setGeneratingDialogue(false);
    }
  }, [job?.job_id, refresh]);

  return (
    <main
      data-testid="scene-to-video-page"
      className="px-6 py-8 max-w-6xl mx-auto space-y-10"
    >
      <header className="space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400">
          // SCENE → VIDEO AGENT
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">
          Stills → Veo 3 clips
        </h1>
        <p className="text-sm text-zinc-400 max-w-2xl leading-relaxed">
          Drop up to 20 still images. Each one gets sent through Google
          Veo 3 image-to-video and returns an 8-second animated clip.
          Per-scene prompts override the shared default.
        </p>
      </header>

      <UploaderCard
        files={files}
        previews={previews}
        prompts={prompts}
        defaultPrompt={defaultPrompt}
        setDefaultPrompt={setDefaultPrompt}
        title={title}
        setTitle={setTitle}
        onPick={onPickFiles}
        onRemove={removeScene}
        onMove={moveScene}
        onClear={clearAll}
        setPromptAt={(i, v) => setPrompts((arr) => {
          const next = [...arr]; next[i] = v; return next;
        })}
        onPromptsFromDoc={(parsed) => {
          setParsedDocPrompts(parsed);
          // Auto-detect the starting scene by sniffing the first
          // file's name for a "scene N" pattern. Falls back to 1.
          let detectedStart = 1;
          const first = files[0]?.name || "";
          const m = first.match(/scene[\s_\-]*0*(\d{1,3})/i);
          if (m) detectedStart = Math.max(1, Number(m[1]));
          setDescStartScene(detectedStart);
          // Apply the mapping now: prompts[i] = parsed[start-1+i].
          setPrompts((arr) => {
            const n = Math.max(arr.length, parsed.length);
            const next = Array(n).fill("");
            for (let i = 0; i < n; i += 1) {
              const srcIdx = detectedStart - 1 + i;
              next[i] = srcIdx >= 0 && srcIdx < parsed.length
                ? parsed[srcIdx]
                : (arr[i] || "");
            }
            return next;
          });
        }}
        parsedDocPrompts={parsedDocPrompts}
        descStartScene={descStartScene}
        onChangeDescStartScene={(n) => {
          const start = Math.max(1, Math.min(parsedDocPrompts.length || 1, Number(n) || 1));
          setDescStartScene(start);
          if (parsedDocPrompts.length === 0) return;
          setPrompts((arr) => {
            const total = Math.max(arr.length, files.length);
            const next = Array(total).fill("");
            for (let i = 0; i < total; i += 1) {
              const srcIdx = start - 1 + i;
              next[i] = srcIdx >= 0 && srcIdx < parsedDocPrompts.length
                ? parsedDocPrompts[srcIdx]
                : (arr[i] || "");
            }
            return next;
          });
        }}
        onSubmit={submit}
        submitting={submitting || running}
        uploading={submitting}
        uploadPct={uploadPct}
        videoProvider={videoProvider}
        onChangeVideoProvider={setVideoProvider}
      />

      {job && (
        <JobView
          job={job}
          onRefresh={refresh}
          onCancel={onCancelJob}
          cancelling={cancelling}
          onRetryFailed={onRetryFailed}
          retrying={retrying}
          onGenerateDialogue={onGenerateDialogue}
          generatingDialogue={generatingDialogue}
        />
      )}

      {/* Provider Shootout — test 1 scene on every model */}
      {files.length > 0 && (
        <ShootoutLaunchCard
          files={files}
          previews={previews}
          prompts={prompts}
          defaultPrompt={defaultPrompt}
          sceneIdx={shootoutSceneIdx}
          onChangeSceneIdx={setShootoutSceneIdx}
          onOpen={() => setShootoutOpen(true)}
          disabled={submitting || running}
        />
      )}
      <ProviderShootoutModal
        open={shootoutOpen}
        onClose={() => setShootoutOpen(false)}
        sourceImageUrl={previews[shootoutSceneIdx] || ""}
        sourcePrompt={
          (prompts[shootoutSceneIdx] || "").trim() || defaultPrompt
        }
        onPick={(pid) => setVideoProvider(pid)}
      />
    </main>
  );
}

// Small launch card — sits below the uploader, lets the user pick
// which already-uploaded still to test across every model.
function ShootoutLaunchCard({
  files, previews, prompts, defaultPrompt, sceneIdx,
  onChangeSceneIdx, onOpen, disabled,
}) {
  const safeIdx = Math.max(0, Math.min(files.length - 1, sceneIdx));
  const previewUrl = previews[safeIdx];
  const promptPreview = (prompts[safeIdx] || "").trim() || defaultPrompt;
  return (
    <section
      data-testid="stv-shootout-card"
      className="border border-yellow-400/40 bg-yellow-400/[0.03] rounded-sm p-5 space-y-3"
    >
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 font-bold">
            // PROVIDER SHOOTOUT
          </div>
          <div className="font-display text-2xl font-black text-white tracking-tight">
            Test one scene on every model
          </div>
          <p className="text-xs text-zinc-400 max-w-2xl leading-relaxed mt-1">
            Animates one of your stills through Veo 3, Kling 2.5, and
            Seedance 2.0 in parallel. Pick the result you like best and
            apply it to the rest of the job.
          </p>
        </div>
        <button
          type="button"
          data-testid="stv-shootout-open"
          onClick={onOpen}
          disabled={disabled}
          className="font-display text-sm font-black uppercase tracking-wide bg-yellow-400 text-black hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed px-5 py-2.5 rounded-sm"
        >
          ▶ Run shootout
        </button>
      </div>
      <div className="flex items-end gap-3 flex-wrap">
        <div className="space-y-1">
          <label
            htmlFor="stv-shootout-scene"
            className="block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500"
          >
            Test scene
          </label>
          <select
            id="stv-shootout-scene"
            data-testid="stv-shootout-scene"
            value={safeIdx}
            onChange={(e) => onChangeSceneIdx(Number(e.target.value))}
            disabled={disabled}
            className="bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-xs text-zinc-100"
          >
            {files.map((f, i) => (
              <option key={i} value={i}>
                Scene #{i + 1} — {(f.name || "").slice(0, 40)}
              </option>
            ))}
          </select>
        </div>
        {previewUrl && (
          <div className="flex items-center gap-3">
            <img
              src={previewUrl}
              alt={`Scene ${safeIdx + 1}`}
              className="w-32 aspect-video object-cover border border-zinc-800 rounded-sm"
            />
            <div className="text-[11px] text-zinc-400 max-w-md line-clamp-3 italic">
              “{promptPreview || "(no prompt set — default will be used)"}”
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ---------- Uploader Card ----------
function UploaderCard({
  files, previews, prompts, defaultPrompt, setDefaultPrompt,
  title, setTitle, onPick, onRemove, onMove, onClear, setPromptAt,
  onPromptsFromDoc,
  parsedDocPrompts = [],
  descStartScene = 1,
  onChangeDescStartScene,
  videoProvider = "",
  onChangeVideoProvider,
  onSubmit, submitting, uploading = false, uploadPct,
}) {
  return (
    <section
      data-testid="stv-uploader"
      className="border border-zinc-800 rounded-sm p-5 bg-black space-y-5"
    >
      {uploading && (
        <JobProgressBanner
          stage="uploading"
          uploadPct={uploadPct}
          total={files.length}
          testIdPrefix="stv-upload-progress"
          jobLabel={`${files.length} still${files.length === 1 ? "" : "s"} · ${title || "untitled"}`}
        />
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            // STEP 1 · UPLOAD STILLS
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-0.5">
            {files.length}/{MAX_SCENES} added
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <UploadScenePromptsButton
            testIdPrefix="stv-upload-prompts"
            onPrompts={(parsed) => onPromptsFromDoc?.(parsed)}
            disabled={submitting}
            label="Upload scene descriptions"
          />
          <label
            className={`font-mono text-[10px] uppercase tracking-[0.25em] cursor-pointer text-yellow-300 hover:text-yellow-200 border border-yellow-400/50 hover:border-yellow-400 px-3 py-2 rounded-sm ${
              submitting || files.length >= MAX_SCENES ? "opacity-50 cursor-not-allowed" : ""
            }`}
            data-testid="stv-add-files-label"
          >
            + Add stills
            <input
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={onPick}
              data-testid="stv-file-input"
              disabled={submitting || files.length >= MAX_SCENES}
            />
          </label>
          {files.length > 0 && (
            <button
              type="button"
              onClick={onClear}
              disabled={submitting}
              data-testid="stv-clear-btn"
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-red-300 border border-zinc-800 hover:border-red-500/50 px-3 py-2 rounded-sm"
            >
              × Clear
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[240px]">
          <label
            htmlFor="stv-title"
            className="block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1"
          >
            JOB TITLE (optional)
          </label>
          <input
            id="stv-title"
            data-testid="stv-title-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 200))}
            placeholder="e.g. Bella's birthday — scenes 1–8"
            disabled={submitting}
            className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-sm text-zinc-100"
          />
        </div>
        <VideoProviderSelect
          testIdPrefix="stv-video-provider"
          value={videoProvider}
          onChange={onChangeVideoProvider}
          disabled={submitting}
        />
      </div>

      <div>
        <label
          htmlFor="stv-default-prompt"
          className="block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1"
        >
          DEFAULT VEO3 PROMPT — applied to every scene unless overridden below
        </label>
        <textarea
          id="stv-default-prompt"
          data-testid="stv-default-prompt"
          value={defaultPrompt}
          onChange={(e) => setDefaultPrompt(e.target.value.slice(0, 1000))}
          disabled={submitting}
          rows={2}
          className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-xs text-zinc-100 leading-relaxed"
        />
      </div>

      {parsedDocPrompts.length > 0 && (
        <div
          data-testid="stv-scene-offset-bar"
          className="flex flex-wrap items-center gap-3 border border-yellow-400/30 bg-yellow-400/5 rounded-sm px-3 py-2"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-300 font-bold">
            ● {parsedDocPrompts.length} scene description{parsedDocPrompts.length === 1 ? "" : "s"} loaded
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 flex items-center gap-2">
            <label htmlFor="stv-scene-offset">
              First still = source Scene #
            </label>
            <input
              id="stv-scene-offset"
              data-testid="stv-scene-offset-input"
              type="number"
              min={1}
              max={parsedDocPrompts.length}
              value={descStartScene}
              onChange={(e) => onChangeDescStartScene?.(e.target.value)}
              disabled={submitting}
              className="w-16 bg-black border border-yellow-400/40 focus:border-yellow-400 focus:outline-none rounded-sm px-2 py-1 text-xs text-yellow-200 font-mono text-center"
            />
            <span className="text-zinc-500">
              → stills map to scenes{" "}
              <span className="text-zinc-200">{descStartScene}</span>
              {files.length > 1 && (
                <>–<span className="text-zinc-200">{Math.min(descStartScene + files.length - 1, parsedDocPrompts.length)}</span></>
              )}
            </span>
          </div>
        </div>
      )}

      {files.length === 0 ? (
        <div className="border border-dashed border-zinc-800 rounded-sm py-12 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            no stills yet · click + Add stills above
          </div>
        </div>
      ) : (
        <div
          data-testid="stv-pending-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          {files.map((file, idx) => (
            <PendingScene
              key={idx}
              idx={idx}
              file={file}
              previewUrl={previews[idx]}
              promptValue={prompts[idx] || ""}
              sourceSceneNumber={
                parsedDocPrompts.length > 0
                  ? descStartScene + idx
                  : null
              }
              onPrompt={(v) => setPromptAt(idx, v)}
              onRemove={() => onRemove(idx)}
              onMove={(dir) => onMove(idx, dir)}
              disabled={submitting}
              isFirst={idx === 0}
              isLast={idx === files.length - 1}
            />
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 justify-end pt-1">
        {submitting && uploadPct > 0 && uploadPct < 100 && (
          <span
            data-testid="stv-upload-progress"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400"
          >
            uploading {uploadPct}%…
          </span>
        )}
        <button
          type="button"
          data-testid="stv-submit-btn"
          onClick={onSubmit}
          disabled={submitting || files.length === 0}
          className={`font-display text-sm font-black uppercase tracking-wide px-5 py-2.5 rounded-sm transition-colors ${
            !submitting && files.length > 0
              ? "bg-yellow-400 text-black hover:bg-yellow-300"
              : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
          }`}
        >
          {submitting
            ? "▸ Working…"
            : `▶ Animate ${files.length || ""} scene${files.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </section>
  );
}

function PendingScene({
  idx, file, previewUrl, promptValue, onPrompt,
  sourceSceneNumber = null,
  onRemove, onMove, disabled, isFirst, isLast,
}) {
  return (
    <div
      data-testid={`stv-pending-${idx}`}
      className="border border-zinc-800 rounded-sm bg-black/40 overflow-hidden"
    >
      <div className="aspect-video bg-zinc-950 relative border-b border-zinc-800">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={`Scene ${idx + 1}`}
            className="w-full h-full object-cover"
          />
        ) : null}
        <div className="absolute top-2 left-2 font-mono text-[10px] uppercase tracking-[0.2em] bg-black/80 border border-zinc-800 text-zinc-200 px-2 py-0.5 rounded-sm">
          {sourceSceneNumber ? (
            <>
              Scene {sourceSceneNumber}
              <span className="text-zinc-500 ml-1">· #{idx + 1}</span>
            </>
          ) : (
            <>Scene {idx + 1}</>
          )}
        </div>
        <div className="absolute top-2 right-2 flex gap-1">
          <button
            type="button"
            data-testid={`stv-pending-${idx}-up`}
            onClick={() => onMove(-1)}
            disabled={disabled || isFirst}
            className="font-mono text-[10px] bg-black/80 border border-zinc-700 text-zinc-200 hover:text-yellow-300 disabled:text-zinc-600 px-1.5 py-0.5 rounded-sm"
            title="Move up"
          >
            ↑
          </button>
          <button
            type="button"
            data-testid={`stv-pending-${idx}-down`}
            onClick={() => onMove(1)}
            disabled={disabled || isLast}
            className="font-mono text-[10px] bg-black/80 border border-zinc-700 text-zinc-200 hover:text-yellow-300 disabled:text-zinc-600 px-1.5 py-0.5 rounded-sm"
            title="Move down"
          >
            ↓
          </button>
          <button
            type="button"
            data-testid={`stv-pending-${idx}-remove`}
            onClick={onRemove}
            disabled={disabled}
            className="font-mono text-[10px] bg-black/80 border border-red-500/50 text-red-300 hover:text-red-200 px-1.5 py-0.5 rounded-sm"
            title="Remove"
          >
            ×
          </button>
        </div>
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 truncate">
            {file?.name || ""}
          </div>
          {promptValue?.trim() && (
            <span
              data-testid={`stv-pending-${idx}-prompt-badge`}
              className="font-mono text-[8px] uppercase tracking-[0.25em] text-yellow-300 border border-yellow-400/40 px-1.5 py-0.5 rounded-sm shrink-0"
              title="Prompt loaded — sent to Veo 3 as this scene's description"
            >
              ● scene description
            </span>
          )}
        </div>
        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-yellow-400/80">
          {sourceSceneNumber
            ? `SOURCE SCENE ${sourceSceneNumber} DESCRIPTION · SENT TO VEO 3`
            : `SCENE ${idx + 1} DESCRIPTION · SENT TO VEO 3`}
        </div>
        <textarea
          data-testid={`stv-pending-${idx}-prompt`}
          rows={3}
          value={promptValue}
          onChange={(e) => onPrompt(e.target.value.slice(0, 1000))}
          placeholder="Describe what should happen in this scene (optional — overrides default). Upload a doc above to auto-fill."
          disabled={disabled}
          className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2 py-1 text-xs text-zinc-100 leading-relaxed resize-y"
        />
      </div>
    </div>
  );
}

// ---------- Running / Completed Job View ----------
function JobView({ job, onRefresh, onCancel, cancelling, onRetryFailed, retrying, onGenerateDialogue, generatingDialogue }) {
  const scenes = job.scenes || [];
  const readyCount = scenes.filter((s) => s.status === "ready").length;
  const failedCount = scenes.filter((s) => s.status === "failed").length;
  const animatingCount = scenes.filter((s) => s.status === "animating").length;
  const queuedCount = scenes.filter((s) => s.status === "queued").length;
  const cancelledCount = scenes.filter((s) => s.status === "cancelled").length;

  // Pick the right banner stage so the colour + label match reality.
  let bannerStage = "animating";
  if (job.status === "cancelled") bannerStage = "failed";
  else if (job.status === "failed") bannerStage = "failed";
  else if (job.status === "complete") bannerStage = "complete";
  else if (job.stitch_status === "running") bannerStage = "stitching";
  else if (animatingCount > 0) bannerStage = "animating";
  else if (queuedCount > 0) bannerStage = "queued";

  const statusBadge = (() => {
    if (job.status === "complete") return { text: "complete", cls: "bg-emerald-400 text-black" };
    if (job.status === "failed") return { text: "failed", cls: "bg-red-500 text-white" };
    if (job.status === "cancelled") return { text: "cancelled", cls: "bg-zinc-600 text-white" };
    return { text: "running…", cls: "bg-yellow-400 text-black animate-pulse" };
  })();

  const canCancel = job.status === "running";
  const canRetry = failedCount > 0 && job.status !== "running" && job.status !== "cancelled";
  const canRewriteDialogue = scenes.length > 0 && job.status !== "running";

  return (
    <section
      data-testid="stv-job-view"
      className="border border-zinc-800 rounded-sm p-5 bg-black space-y-5"
    >
      <JobProgressBanner
        stage={bannerStage}
        total={scenes.length}
        ready={readyCount}
        animating={animatingCount}
        queued={queuedCount}
        failed={failedCount}
        jobLabel={`Job ${job.job_id}${job.title ? ` · ${job.title}` : ""}`}
        testIdPrefix="stv-job-progress"
      />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 font-bold">
            // STEP 2 · LIVE PROGRESS
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">
            Job <span className="text-zinc-300">{job.job_id}</span>
            {job.title ? ` · ${job.title}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid="stv-job-status"
            className={`font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 rounded-sm font-bold ${statusBadge.cls}`}
          >
            {statusBadge.text}
          </span>
          {canCancel && (
            <button
              type="button"
              data-testid="stv-job-cancel"
              onClick={onCancel}
              disabled={cancelling}
              title="Stop scheduling new scenes. Any scene currently animating finishes on its own."
              className="font-mono text-[10px] uppercase tracking-[0.25em] bg-red-500/20 border border-red-500/60 text-red-300 hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1 rounded-sm transition-colors"
            >
              {cancelling ? "■ stopping…" : "■ stop"}
            </button>
          )}
          <button
            type="button"
            data-testid="stv-job-refresh"
            onClick={onRefresh}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-yellow-300 border border-zinc-800 hover:border-yellow-400/50 px-2 py-1 rounded-sm"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {canRewriteDialogue && (
        <DialogueRewriteBar
          failedCount={failedCount}
          totalCount={scenes.length}
          onRewrite={onGenerateDialogue}
          generating={generatingDialogue}
        />
      )}

      {canRetry && (
        <RetryFailedBar
          failedCount={failedCount}
          currentProvider={job.video_provider}
          onRetry={onRetryFailed}
          retrying={retrying}
        />
      )}

      <div
        data-testid="stv-scene-grid"
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
      >
        {scenes.map((s) => (
          <JobScene key={s.idx} scene={s} />
        ))}
      </div>
    </section>
  );
}

function DialogueRewriteBar({ failedCount, totalCount, onRewrite, generating }) {
  return (
    <div
      data-testid="stv-dialogue-bar"
      className="border border-cyan-400/30 bg-cyan-400/5 rounded-sm p-4 flex flex-wrap items-center gap-3"
    >
      <div className="flex-1 min-w-[220px]">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300 font-bold">
          // add character dialogue
        </div>
        <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
          Rewrite each scene's prompt so Veo / Kling / Seedance generate
          synced character speech + lip-sync — not just ambient sound.
          Then click Retry to re-render with audio.
        </div>
      </div>
      {failedCount > 0 && (
        <button
          type="button"
          data-testid="stv-dialogue-failed-btn"
          disabled={generating}
          onClick={() => onRewrite({ onlyFailed: true })}
          className="font-mono text-[10px] uppercase tracking-[0.25em] border border-cyan-400/60 text-cyan-200 hover:bg-cyan-400/10 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 rounded-sm transition-colors"
        >
          {generating ? "✎ writing…" : `✎ rewrite ${failedCount} failed`}
        </button>
      )}
      <button
        type="button"
        data-testid="stv-dialogue-all-btn"
        disabled={generating}
        onClick={() => onRewrite({ onlyFailed: false })}
        className="font-mono text-[10px] uppercase tracking-[0.25em] bg-cyan-400 hover:bg-cyan-300 text-black font-bold px-4 py-2 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {generating ? "✎ writing…" : `✎ rewrite all ${totalCount}`}
      </button>
    </div>
  );
}

function RetryFailedBar({ failedCount, currentProvider, onRetry, retrying }) {
  const [providers, setProviders] = useState([]);
  const [picked, setPicked] = useState(currentProvider || "");

  useEffect(() => {
    let cancel = false;
    listVideoProviders()
      .then((res) => {
        if (cancel) return;
        const list = (res?.providers || []).filter((p) => p.available);
        setProviders(list);
        if (!picked && list.length) {
          // Default to the OTHER provider — the one that wasn't the
          // original failure source — so retrying is meaningful.
          const other = list.find((p) => p.id !== currentProvider);
          setPicked((other || list[0]).id);
        }
      })
      .catch(() => { /* non-fatal */ });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProvider]);

  return (
    <div
      data-testid="stv-retry-failed-bar"
      className="border border-yellow-400/30 bg-yellow-400/5 rounded-sm p-4 flex flex-wrap items-center gap-3"
    >
      <div className="flex-1 min-w-[220px]">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 font-bold">
          // {failedCount} scene{failedCount === 1 ? "" : "s"} failed
        </div>
        <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
          Retry only the failed scenes — the {failedCount > 0 ? "succeeded" : ""} ones stay as-is.
          Switch to a different provider if one model rejected the image.
        </div>
      </div>
      <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
        Provider:
        <select
          data-testid="stv-retry-provider-select"
          value={picked}
          onChange={(e) => setPicked(e.target.value)}
          className="bg-black border border-zinc-700 text-zinc-200 font-mono text-xs px-2 py-1 rounded-sm focus:border-yellow-400 focus:outline-none"
        >
          {providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}{p.id === currentProvider ? " (same as failed)" : ""}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        data-testid="stv-retry-failed-btn"
        disabled={retrying || !picked}
        onClick={() => onRetry(picked)}
        className="font-mono text-[10px] uppercase tracking-[0.25em] bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-4 py-2 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {retrying ? "↻ retrying…" : `↻ retry ${failedCount} failed`}
      </button>
    </div>
  );
}

function JobScene({ scene }) {
  const url = (raw) => {
    if (!raw) return "";
    if (raw.startsWith("http")) return raw;
    if (raw.startsWith("/")) return `${API_BASE}${raw}`;
    return raw;
  };
  const isReady = scene.status === "ready" && !!scene.video_url;
  const isFailed = scene.status === "failed";
  const isCancelled = scene.status === "cancelled";
  const isAnimating = scene.status === "animating";

  return (
    <div
      data-testid={`stv-scene-${scene.idx}`}
      data-status={scene.status}
      className={`border rounded-sm overflow-hidden bg-black/40 transition-colors ${
        isReady
          ? "border-emerald-400/50"
          : isFailed
            ? "border-red-500/50"
            : isCancelled
              ? "border-zinc-600/50"
              : isAnimating
                ? "border-yellow-400/50"
                : "border-zinc-800"
      }`}
    >
      <div className="aspect-video bg-zinc-950 relative">
        {isReady ? (
          <video
            key={scene.video_url}
            src={url(scene.video_url)}
            controls
            playsInline
            preload="metadata"
            poster={url(scene.image_url)}
            data-testid={`stv-scene-${scene.idx}-video`}
            className="w-full h-full object-cover"
          />
        ) : (
          <>
            {scene.image_url ? (
              <img
                src={url(scene.image_url)}
                alt={`Scene ${scene.idx + 1}`}
                className="w-full h-full object-cover opacity-80"
              />
            ) : null}
            {isAnimating && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60">
                <div className="w-8 h-8 border-2 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin" />
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400">
                  animating · veo 3
                </div>
              </div>
            )}
            {scene.status === "queued" && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                  queued
                </div>
              </div>
            )}
            {isFailed && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-3 text-center bg-black/70">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-red-400">
                  failed
                </div>
                <div className="text-[10px] text-zinc-300 line-clamp-3">
                  {scene.error || "Veo 3 failed"}
                </div>
              </div>
            )}
            {isCancelled && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-3 text-center bg-black/70">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">
                  cancelled
                </div>
                <div className="text-[10px] text-zinc-500">
                  Stopped before animating
                </div>
              </div>
            )}
          </>
        )}
        <div className="absolute top-2 left-2 font-mono text-[10px] uppercase tracking-[0.2em] bg-black/80 border border-zinc-800 text-zinc-200 px-2 py-0.5 rounded-sm">
          Scene {scene.idx + 1}
        </div>
        {isReady && (
          <a
            href={url(scene.video_url)}
            download
            data-testid={`stv-scene-${scene.idx}-download`}
            className="absolute bottom-2 right-2 font-mono text-[10px] uppercase tracking-[0.2em] bg-emerald-400 text-black hover:bg-emerald-300 px-2 py-0.5 rounded-sm font-bold"
          >
            ⬇ mp4
          </a>
        )}
      </div>
      {scene.prompt ? (
        <div
          className="px-3 py-2 text-[11px] text-zinc-300 leading-relaxed border-t border-zinc-800 italic line-clamp-2"
          title={scene.prompt}
        >
          “{scene.prompt}”
        </div>
      ) : null}
    </div>
  );
}
